import { useEffect, useState } from 'react';
import Icon from '../app/components/Icon';
import { MEAL_LABELS, grams, kcal } from '../app/lib/format';
import { useApp } from '../app/lib/AppContext';

const INPUT = 'input h-11 w-full rounded-[14px] border-line text-base';
const BUTTON = 'btn h-11 rounded-[14px] border-line bg-base-100 px-4';

function status(dish, promoteUsers) {
  if (dish.hidden) return { label: 'Đã ẩn', chip: 'bg-base-300 text-muted' };
  if (dish.in_use) return { label: dish.approved && dish.users_count < promoteUsers ? 'Đang gợi ý · đã duyệt' : 'Đang gợi ý', chip: 'bg-[#e3f1e9] text-[#17804f]' };
  return { label: `Chờ thêm ${promoteUsers - dish.users_count} người`, chip: 'bg-[#fdf3d4] text-[#8a6100]' };
}

function EditForm({ dish, onSave, onCancel }) {
  const [values, setValues] = useState({ name: dish.name, keyword: dish.keyword, calories: dish.calories, protein_g: dish.protein_g, tip: dish.tip || '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const field = (key, label, props = {}) => (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-semibold">{label}</span>
      <input className={INPUT} value={values[key]} onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))} {...props} />
    </label>
  );
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave(values);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-[14px] bg-base-200 p-3">
      {field('name', 'Tên món', { required: true })}
      {field('keyword', 'Từ khoá tìm kiếm', { required: true })}
      <div className="grid grid-cols-2 gap-3">
        {field('calories', 'kcal', { type: 'number', inputMode: 'numeric', required: true })}
        {field('protein_g', 'Đạm (g)', { type: 'number', inputMode: 'numeric', required: true })}
      </div>
      {field('tip', 'Mẹo gọi món')}
      <p className="text-xs text-muted">Sau khi sửa, số liệu giữ nguyên, không lấy trung bình theo các lần đặt sau nữa.</p>
      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary h-11 rounded-[14px] border-0 px-5 font-bold" disabled={busy}>Lưu</button>
        <button type="button" className={BUTTON} onClick={onCancel}>Huỷ</button>
      </div>
    </form>
  );
}

function DishRow({ dish, promoteUsers, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = status(dish, promoteUsers);
  const run = async (attrs) => {
    setBusy(true);
    try {
      await onUpdate(dish.id, attrs);
    } catch (e) {
      window.alert(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="flex flex-col gap-2 rounded-[20px] bg-base-100 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-bold">{dish.name}</h2>
          <p className="tabular text-sm text-muted">
            ≈ {kcal(dish.calories)} kcal · <b className="text-protein">{grams(dish.protein_g)} đạm</b> · {dish.meals.map((m) => MEAL_LABELS[m]).join(', ')}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${s.chip}`}>{s.label}</span>
      </div>
      {dish.tip && <p className="flex items-start gap-2 text-sm"><Icon name="lightbulb" className="mt-0.5 size-4 shrink-0 text-brand-text" /> {dish.tip}</p>}
      <p className="text-xs text-muted">
        Tìm: "{dish.keyword}" · {dish.users_count} người đã đặt ({dish.picks} lần){dish.edited ? ' · đã sửa tay' : ''}
      </p>
      {editing ? (
        <EditForm dish={dish} onCancel={() => setEditing(false)}
          onSave={async (values) => { await onUpdate(dish.id, values); setEditing(false); }} />
      ) : (
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className={BUTTON} onClick={() => setEditing(true)}><Icon name="pencil" className="size-4" /> Sửa</button>
          {!dish.in_use && !dish.hidden && (
            <button type="button" className={BUTTON} disabled={busy} onClick={() => run({ approved: true })}>
              <Icon name="check" className="size-4" /> Dùng ngay
            </button>
          )}
          <button type="button" className={BUTTON} disabled={busy} onClick={() => run({ hidden: !dish.hidden })}>
            {dish.hidden ? 'Hiện lại' : 'Ẩn'}
          </button>
        </div>
      )}
    </article>
  );
}

// Admin panel for Đặt ngoài's self-growing library: dishes the AI suggested that people ordered.
// The server checks the admin session; this page only hides itself.
export default function TakeoutDishesPage() {
  const { api, admin } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (admin) api.get('/admin/takeout_dishes').then(setData).catch((e) => setError(e.message));
  }, [api, admin]);

  if (!admin) return <p className="rounded-[20px] bg-base-100 p-5 text-sm">Trang này chỉ dành cho quản trị viên.</p>;

  const update = async (id, attrs) => {
    const updated = await api.patch(`/admin/takeout_dishes/${id}`, attrs);
    setData((d) => ({ ...d, dishes: d.dishes.map((x) => (x.id === id ? updated : x)) }));
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-[22px] font-bold">Món đặt ngoài đã học</h1>
      {data && (
        <p className="text-sm text-muted">
          Món AI gợi ý mà {data.promote_users} người khác nhau đã đặt sẽ tự vào thư viện gợi ý miễn phí, bên cạnh {data.seed_count} món có sẵn.
          Bạn có thể cho dùng sớm, ẩn món không hợp, hoặc sửa số liệu.
        </p>
      )}
      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!data && !error && <div className="skeleton h-48 rounded-[20px]" />}
      {data?.dishes.length === 0 && (
        <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">
          Chưa có món nào. Khi người dùng đặt món từ "Gợi ý khác bằng AI", món đó sẽ hiện ở đây.
        </p>
      )}
      {data?.dishes.map((dish) => <DishRow key={dish.id} dish={dish} promoteUsers={data.promote_users} onUpdate={update} />)}
    </div>
  );
}
